import React from 'react';
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    ThemeProvider,
    Box,
    Typography,
    Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Spinner from './spinner';
import { useTranslation } from 'react-i18next';
import vadoTheme from '../main/VadoThemeMui5'; // Import VadoTheme for mui 5
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import PropTypes from 'prop-types';
dayjs.extend(utc);

/**
 * Dialog to show stored PDF documents
 * @param {boolean} open - Whether the dialog is open.
 * @param {function} onClose - Function to call when the dialog is closed.
 * @param {Array<{fileName: string, fileUrl: string, insertDate: string}>} storedPdfs - List of stored PDF documents. Null if loading.
 */
export default function StoredPdfDialog({ open, onClose, storedPdfs }) {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <ThemeProvider theme={vadoTheme}>
            <Dialog
                open={open}
                onClose={onClose}
                // make mobile use full width, desktop use a fixed ~500px width
                fullWidth={isMobile}
                maxWidth={false} // disable built-in breakpoint sizing so PaperProps width is used
                PaperProps={{
                    style: {
                        width: isMobile ? '100%' : 500, // desktop ≈500px, mobile 100%
                        maxWidth: '95vw', // ensure it never overflows viewport
                        minWidth: isMobile ? undefined : 360, // optional min width for desktop
                    },
                }}>
                <DialogTitle>{t('buttons.viewStoredPdf')}</DialogTitle>
                <DialogContent>
                    {storedPdfs == null && <Spinner padding="20px" title={t('general.loading')} />}
                    {storedPdfs != null && storedPdfs.length === 0 && (
                        <Typography variant="body2" color="textSecondary">
                            {t('general.noStoredPdfFiles')}
                        </Typography>
                    )}
                    {storedPdfs != null && storedPdfs.length > 0 && (
                        <Box display="flex" flexDirection="column" gap={2} mt={1}>
                            {storedPdfs.map((pdfDoc, idx) => (
                                <Box
                                    key={idx}
                                    display="flex"
                                    flexDirection="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    border={1}
                                    borderColor="grey.300"
                                    borderRadius={4}
                                    p={2}>
                                    <Link
                                        href={pdfDoc.fileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        underline="hover"
                                        color="inherit"
                                        style={{
                                            flex: 1,
                                            display: 'block',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            marginRight: 12,
                                        }}>
                                        <Typography variant="body2" color="textPrimary" noWrap>
                                            {pdfDoc.fileName}
                                        </Typography>
                                    </Link>

                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        style={{ flexShrink: 0, marginLeft: 12 }}>
                                        {dayjs.utc(pdfDoc.insertDate).local().format('DD.MM.YYYY HH:mm')}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} size="small" color="secondary" variant="contained">
                        {t('buttons.close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
}

StoredPdfDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    storedPdfs: PropTypes.arrayOf(
        PropTypes.shape({
            fileName: PropTypes.string,
            fileUrl: PropTypes.string,
            insertDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
        })
    ),
};

StoredPdfDialog.defaultProps = {
    open: false,
    storedPdfs: null,
};
